import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import handleAsyncThunk from '../handleAsyncThunk';
import productApi from '../../service/productApi';
import { showSuccessSnackbar, showErrorSnackbar } from './snackbarSlice';

export const createNewProduct = createAsyncThunk(
  'products/createNewProduct',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await productApi.addNewProduct(data);
      if (response.status === 201) {
        dispatch(showSuccessSnackbar({ title: 'Product created successfully' }));
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || 'Something went wrong' }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const getProducts = createAsyncThunk(
  'products/getProducts',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await productApi.getProducts(data);
      if (response.status === 200) {
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || 'Something went wrong' }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSearchedProducts = createAsyncThunk(
  'products/fetchSearchedProducts',
  async (queryObj, { rejectWithValue }) => {
    try {
      const response = await productApi.searchProducts(queryObj);
      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue(response.data?.message || 'Failed to search products');
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Something went wrong');
    }
  }
);

export const getProductById = createAsyncThunk(
  'products/getProductById',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await productApi.getProductById(data);
      if (response.status === 200) {
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || 'Something went wrong' }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await productApi.updateProduct(data);
      if (response.status === 200) {
        dispatch(showSuccessSnackbar({ title: 'Product updated successfully' }));
        return response?.data;
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || 'Something went wrong' }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await productApi.deleteProduct(data);
      if (response.status === 204) {
        dispatch(showSuccessSnackbar({ title: 'Product deleted successfully' }));
        return data; // Return id for state update
      } else {
        dispatch(showErrorSnackbar({ title: response?.message || 'Something went wrong' }));
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ title: error?.message }));
      return rejectWithValue(error.message);
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState: {
    data: null,
    status: null,
    error: null,
    isLoading: false,
    products: [],
    productList: [],
    productListLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    handleAsyncThunk(builder, createNewProduct, 'data', 'error', 'productLoading');
    handleAsyncThunk(builder, getProducts, 'products', 'error', 'productsListLoading');
    handleAsyncThunk(builder, getProductById, 'productById', 'error', 'productByIdLoading');
    handleAsyncThunk(builder, deleteProduct, 'data', 'error', 'deleteProductLoading');
    handleAsyncThunk(builder, updateProduct, 'data', 'error', 'updateProductLoading');
    handleAsyncThunk(builder, fetchSearchedProducts, 'productList', 'error', 'productListLoading');
  },
});

export default productSlice.reducer;